import { useState } from "react"
import type { PreviewProject, PreviewProjectFormResponse } from "@/schema"
import { Avatar, AvatarFallback, AvatarImage, Badge, Button } from "@ui/shad"
import { cn } from "@ui/utils"
import { Check, Copy, ExternalLink, Home, User } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link } from "@/components/book-link"
import { formatCurrencyDisplay } from "@/components/custom-fields/number-format"

import PeekViewSkeleton from "./skeleton"

const statusLabels: Record<PreviewProject["status"], string> = {
  in_progress: "In progress",
  overdue: "Overdue",
  completed: "Completed",
  cancelled: "Cancelled",
}

const statusVariants: Record<PreviewProject["status"], "default" | "secondary" | "destructive" | "outline"> = {
  in_progress: "default",
  overdue: "destructive",
  completed: "secondary",
  cancelled: "outline",
}

export default function ProjectPeekView({ projectId, href }: { projectId: string; href: string | null }) {
  const { data: project, isLoading } = usePeekViewData(projectId, "Project") as {
    data: PreviewProject
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton />

  const initials = project.assigned_to?.full_name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

  const milestoneProgresses = [...(project.milestone_progresses || [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  )

  return (
    <div className="flex w-xs flex-col items-stretch gap-2 xl:w-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline">
          <p className="truncate font-medium">{project.name}</p>
          {href && (
            <Link
              href={href}
              className="text-muted-foreground hover:text-link ml-2 transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open in new page"
            >
              <ExternalLink className="size-3.5" strokeWidth={1.5} />
            </Link>
          )}
        </div>
        <Badge variant={statusVariants[project.status]} className="flex-none">
          {statusLabels[project.status]}
        </Badge>
      </div>

      {milestoneProgresses.length > 0 && (
        <div className="flex flex-col gap-1">
          {milestoneProgresses.map((progress) => {
            const progressPercent = Math.min(Math.max(Math.round(progress.percent_complete || 0), 0), 100)
            const barColor = progress.color || "var(--icon-muted)"

            return (
              <div key={progress.project_milestone_id} className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground w-20 truncate">{progress.title}</span>
                <div className="bg-tertiary flex h-1.5 flex-1 overflow-hidden rounded-full">
                  <div className="h-full" style={{ width: `${progressPercent}%`, backgroundColor: barColor }} />
                </div>
                <span className={cn("w-16 text-right", progressPercent === 100 && "text-muted-foreground")}>
                  {progressPercent}% ({progress.completed_tasks}/{progress.total_tasks})
                </span>
              </div>
            )
          })}
        </div>
      )}

      {project.projectable && (
        <div className="flex items-center gap-2">
          <Home className="text-icon size-4 flex-none" />
          <span className="text-tertiary-foreground text-xs">{project.projectable.name}</span>
        </div>
      )}

      {project.assigned_to && (
        <div className="flex items-center gap-2">
          <User className="text-icon size-4 flex-none" />
          <div className="flex items-center gap-1.5">
            <Avatar className="size-4">
              {project.assigned_to.avatar_url && <AvatarImage src={project.assigned_to.avatar_url} />}
              <AvatarFallback className="text-[8px]">{initials}</AvatarFallback>
            </Avatar>
            <span className="text-tertiary-foreground text-xs">{project.assigned_to.full_name}</span>
          </div>
        </div>
      )}

      {project.form_responses && project.form_responses.length > 0 && (
        <div className="flex flex-col gap-0.5 border-t pt-2">
          {project.form_responses.map((r, i) => {
            const displayValue = formatPeekValue(r)
            return (
              <div key={i} className="group/row flex items-center gap-1.5 text-xs">
                <span className="text-muted-foreground">{r.label}:</span>
                <span className="text-tertiary-foreground">{displayValue}</span>
                {displayValue !== "—" && <CopyValueButton value={displayValue} />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function CopyValueButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  return (
    <Button
      variant="ghost"
      size="iconXs"
      className="opacity-0 group-hover/row:opacity-100"
      onClick={(e) => {
        e.stopPropagation()
        navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
    >
      {copied ? <Check className="text-green-accent" /> : <Copy />}
    </Button>
  )
}

function formatPeekValue(r: PreviewProjectFormResponse): string {
  if (r.field_type === "household_record") return r.display_value || "—"
  if (r.value == null) return "—"
  if (r.field_type === "boolean") return r.value === true || r.value === "true" ? "Yes" : "No"
  if (r.field_type === "currency") return formatCurrencyDisplay(r.value as string | number)
  return String(r.value)
}
